import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightbulb, ArrowRight, Bank, SealCheck, UsersThree, MapTrifold } from "@phosphor-icons/react";
import api from "@/lib/api";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const GROUPS = [
  { key: "dormant_branches", label: "Dormant Branches", icon: Bank, why: "Empanelled but no valuation work in 60+ days — the fastest wins usually sit here." },
  { key: "empanelment_renewals", label: "Renewals Due", icon: SealCheck, why: "Reach out before the empanelment lapses and you lose eligibility." },
  { key: "new_branches_in_region", label: "New Branches in Region", icon: MapTrifold, why: "Prospect and contacted branches in territories you already serve." },
  { key: "referral_clients", label: "Referral Opportunities", icon: UsersThree, why: "Clients with two or more valuations — most likely to refer more work." },
];

export default function LeadSuggestions() {
  const [leads, setLeads] = useState(null);
  useEffect(() => { api.get("/outreach/leads").then((r) => setLeads(r.data)).catch(() => setLeads({})); }, []);

  if (!leads) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Finding opportunities…</div>;

  const total = GROUPS.reduce((s, g) => s + (leads[g.key]?.length || 0), 0);

  return (
    <div className="space-y-6" data-testid="lead-suggestions-page">
      <PageHeader
        overline="AI Outreach" title="AI Lead Suggestions"
        subtitle="A system-generated prospect list to seed your next campaign"
        action={<Button asChild className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"><Link to="/app/outreach/new" data-testid="seed-campaign-btn">Start a campaign <ArrowRight size={15} className="ml-1" /></Link></Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {GROUPS.map((g) => <Stat key={g.key} label={g.label} value={leads[g.key]?.length || 0} testid={`stat-${g.key}`} />)}
      </div>

      {total === 0 ? (
        <EmptyState icon={Lightbulb}>No suggestions yet. Once you have branches with empanelment dates and a few jobs logged, opportunities will surface here.</EmptyState>
      ) : (
        <Tabs defaultValue={GROUPS.find((g) => (leads[g.key] || []).length)?.key || GROUPS[0].key}>
          <TabsList className="flex-wrap h-auto">
            {GROUPS.map((g) => <TabsTrigger key={g.key} value={g.key} data-testid={`lead-tab-${g.key}`}>{g.label} ({leads[g.key]?.length || 0})</TabsTrigger>)}
          </TabsList>
          {GROUPS.map((g) => (
            <TabsContent key={g.key} value={g.key}>
              <Card className="p-6 border-border">
                <div className="flex items-start gap-3 mb-4">
                  <g.icon size={20} weight="duotone" className="text-accent mt-0.5 shrink-0" />
                  <p className="text-sm text-muted-foreground">{g.why}</p>
                </div>
                {(leads[g.key] || []).length === 0 ? <EmptyState>Nothing in this bucket right now.</EmptyState> : (
                  <div className="space-y-2">
                    {(leads[g.key] || []).map((x) => (
                      <div key={x.id} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-md bg-muted border border-border" data-testid={`lead-${x.id}`}>
                        <div className="min-w-0">
                          {x.bank_name ? (
                            <Link to={`/app/branches/${x.id}`} className="text-sm font-medium text-primary hover:text-accent truncate block">{x.bank_name} — {x.branch_name}</Link>
                          ) : (
                            <Link to={`/app/clients/${x.id}`} className="text-sm font-medium text-primary hover:text-accent truncate block">{x.name}</Link>
                          )}
                          <div className="text-xs text-muted-foreground truncate">
                            {x.contact_person || x.contact_person === "" ? x.contact_person : ""}
                            {x.phone ? ` · ${x.phone}` : ""}
                            {x.last_job_date ? ` · last job ${x.last_job_date}` : x.bank_name && g.key === "dormant_branches" ? " · no jobs on record" : ""}
                            {x.empanelment_renewal_due ? ` · renewal ${x.empanelment_renewal_due.slice(0, 10)}` : ""}
                            {x.job_count ? ` · ${x.job_count} valuations` : ""}
                            {x.region ? ` · ${x.region}` : ""}
                          </div>
                        </div>
                        {x.expired && <span className="text-[10px] px-2 py-0.5 rounded border bg-rose-100 text-rose-700 border-rose-200 shrink-0">Expired</span>}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
